import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { clearStoredUser, loadStoredUser, setSessionToken, storeUser } from '@/lib/session.ts';
import { logoutSession } from '@/lib/user.api.ts';
import type { DaoUser } from '@/types/dao.ts';

interface AuthState {
  user: DaoUser | null;
  /** 세션 복원이 끝났는지. false 동안에는 라우팅 판단을 미룬다. */
  hydrated: boolean;
}

const initialState: AuthState = { user: null, hydrated: false };

/** 앱 시작 시 세션스토리지의 로그인 정보를 복원한다. */
export const hydrateAuth = createAsyncThunk('auth/hydrate', async () => {
  return loadStoredUser();
});

/**
 * 로그인 성공 시 상태 + 세션스토리지에 함께 반영한다.
 *
 * ★ 로그인 응답에는 서버가 발급한 **API 토큰**이 함께 온다. 그것만 따로 빼서
 *   보관하고(이후 모든 요청의 `Authorization: Bearer`), 회원 정보에는 남기지 않는다.
 *
 * ★ 회원정보 **갱신** 응답에는 토큰이 없다. 그때 지워 버리면 로그인이 풀리므로
 *   **있을 때만 갈아끼운다.**
 */
export const signIn = createAsyncThunk('auth/signIn', async (user: DaoUser) => {
  const { token, ...rest } = user;
  if (token) setSessionToken(token);

  await storeUser(rest);
  return rest;
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    signOut: (state) => {
      clearStoredUser();
      state.user = null;
    },
    /** 포인트 변동 등 부분 갱신 */
    patchUser: (state, action: PayloadAction<Partial<DaoUser>>) => {
      if (state.user) Object.assign(state.user, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrateAuth.fulfilled, (state, action) => {
        state.user = action.payload;
        state.hydrated = true;
      })
      .addCase(hydrateAuth.rejected, (state) => {
        state.user = null;
        state.hydrated = true;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.user = action.payload;
      });
  },
});

export const { signOut, patchUser } = authSlice.actions;

/**
 * 사용자가 직접 누르는 로그아웃.
 *
 * 서버 토큰을 먼저 폐기하고 화면 상태를 지운다. 서버 호출이 실패해도 로그아웃
 * 자체는 진행한다 — 네트워크가 끊겼다고 로그아웃을 못 하면 곤란하다.
 * (그때도 토큰은 30일 뒤 만료된다)
 *
 * 401 로 튕겨서 로그아웃되는 경우(`app.tsx`)는 이미 토큰이 무효하므로 `signOut`
 * 만 쓴다. 서버를 부를 이유가 없다.
 */
export const logout = createAsyncThunk('auth/logout', async (_, { dispatch }) => {
  try {
    await logoutSession();
  } catch {
    // 서버에 닿지 못해도 화면에서는 로그아웃한다.
  }
  dispatch(signOut());
});
export default authSlice.reducer;
